import { alertError } from "@/common/common-function";
import { API_URL } from "@/common/config";
import JwtService from "@/common/jwt.service.js";
import i18n from "@/plugins/i18n";
import { PURGE_AUTH, SET_LOADING } from "@/store/mutations.type";
import axios from "axios";
import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);
const versionID = "20241016";
const customAxios = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    Authorization: `Bearer ${JwtService.getToken()}`,
  },
});
customAxios.interceptors.response.use(
  (response) => {
    vm.$store.commit(SET_LOADING, [false, true]);
    return response;
  },
  (err) => {
    vm.$store.commit(SET_LOADING, [false, true]);
    handlerException(err);
  },
);
customAxios.interceptors.request.use((config) => {
  if (config.setLoading) vm.$store.commit(SET_LOADING, [true, true]);
  config.headers = {
    ...config.headers,
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    Authorization: `Bearer ${JwtService.getToken()}`,
    VersionID: versionID,
  };
  return config;
});

export default {
  query(resource, params, setLoading = true) {
    return customAxios.get(resource, {
      params,
      setLoading,
    });
  },

  get(resource, slug = "", setLoading = true) {
    const resourceNoSlash = resource.endsWith("/")
      ? resource.slice(0, -1)
      : resource;
    if (!slug || slug === "/") {
      return customAxios.get(resourceNoSlash, { setLoading });
    }

    // Support passing query-string as `?a=b` without generating `/ ?a=b`.
    const slugTrimLeft = slug.startsWith("/") ? slug.slice(1) : slug;
    const url =
      slugTrimLeft.startsWith("?") ||
      slugTrimLeft.startsWith("&") ||
      slugTrimLeft.startsWith("#")
        ? `${resourceNoSlash}${slugTrimLeft}`
        : `${resourceNoSlash}/${slugTrimLeft}`;
    return customAxios.get(url, {
      setLoading,
    });
  },

  post(resource, params, setLoading = true) {
    return customAxios.post(`${resource}`, params, {
      setLoading,
    });
  },

  update(resource, slug, params, setLoading = true) {
    return customAxios.put(`${resource}/${slug}`, params, { setLoading });
  },

  put(resource, params, setLoading = true) {
    return customAxios.put(`${resource}`, params, {
      setLoading,
    });
  },

  delete(resource, params, setLoading = true) {
    return customAxios.delete(resource, {
      params,
      setLoading,
    });
  },

  getUsingConfig(resource, slug = "", config = {}, setLoading = true) {
    const resourceNoSlash = resource.endsWith("/")
      ? resource.slice(0, -1)
      : resource;
    if (!slug || slug === "/") {
      return customAxios.get(resourceNoSlash, {
        ...config,
        setLoading,
      });
    }

    const slugTrimLeft = slug.startsWith("/") ? slug.slice(1) : slug;
    const url =
      slugTrimLeft.startsWith("?") ||
      slugTrimLeft.startsWith("&") ||
      slugTrimLeft.startsWith("#")
        ? `${resourceNoSlash}${slugTrimLeft}`
        : `${resourceNoSlash}/${slugTrimLeft}`;
    return customAxios.get(url, {
      ...config,
      setLoading,
    });
  },

  postUsingConfig(resource, params, config = {}, setLoading = true) {
    return customAxios.post(`${resource}`, params, {
      ...config,
      setLoading,
    });
  },

  getBuffer(resource, params, setLoading = true) {
    return customAxios.get(resource, {
      params,
      setLoading,
      responseType: "arraybuffer",
      exposedHeaders: ["Content-Disposition"],
    });
  },

  uploadFileS3ByPostObject(postObject, file) {
    const { url, fields } = postObject;
    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      formData.append(key, fields[key]);
    });
    formData.append("file", file);
    return fetch(url, {
      method: "POST",
      body: formData,
      mode: "no-cors",
    });
  },
};

function handlerException(error) {
  if (!navigator.onLine) {
    alertError({ message: i18n.t("msg.noInternet"), timeout: 10000 });
    window.location.reload();
    throw error;
  }
  if (error.response && error.response.status === 417) {
    location.reload();
    return;
  }
  if (!error.response) {
    alertError({ message: i18n.t("msg.errorSystem"), timeout: 10000 });
    throw error;
  }
  if (error.response.status === 401) {
    alertError({
      message: i18n.t("msg.sessionExpired"),
    });

    vm.$store.commit(PURGE_AUTH);
    vm.$router.push("/login");
  }
  if (error.response.status === 403 || error.response.status === 409) {
    vm.$router.push("/403");
  }
  if (error.response.status === 500) {
    alertError({
      message: i18n.t("msg.errorSystem"),
    });
  }
  throw error;
}
